import { inject, Injectable, InjectionToken } from '@angular/core';

export type PortalLanguage = 'nl' | 'fr' | 'en';

export const SUPPORTED_LANGUAGES: PortalLanguage[] = ['nl', 'fr', 'en'];
export const DEFAULT_LANGUAGE: PortalLanguage = 'nl';
export const LANGUAGE_STORAGE_KEY = 'plantefeve.portal.language';

/** Where a stored language came from: the user's own choice wins over the relation's language. */
export type LanguageSource = 'user' | 'relation';

export interface StoredLanguage {
  lang: PortalLanguage;
  source: LanguageSource;
}

/** Reloads the page; injected so specs can check it without reloading the test runner. */
export const PAGE_RELOAD = new InjectionToken<() => void>('PAGE_RELOAD', {
  providedIn: 'root',
  factory: () => () => window.location.reload(),
});

/** The browser's language, e.g. `nl-BE`. */
export const BROWSER_LANGUAGE = new InjectionToken<() => string>('BROWSER_LANGUAGE', {
  providedIn: 'root',
  factory: () => () => navigator.language,
});

/**
 * Decides the portal language (design.md decision 1): a remembered choice, else the browser language, else Dutch.
 * `LOCALE_ID` and the translations are fixed at start, so a change is stored and applied with a page reload.
 */
@Injectable({
  providedIn: 'root',
})
export class PortalLanguageService {
  private readonly _reload = inject(PAGE_RELOAD);
  private readonly _browserLanguage = inject(BROWSER_LANGUAGE);

  /** The language the portal started in. */
  public readonly current: PortalLanguage = this.initialLanguage();

  public initialLanguage(): PortalLanguage {
    return this.read()?.lang ?? this.browserLanguage() ?? DEFAULT_LANGUAGE;
  }

  /**
   * Follows the language of the customer's relation, unless the user chose one. Reloads only when the
   * language changes and was stored, so a browser that blocks storage does not reload forever.
   */
  public applyRelationLanguage(lang: string | null | undefined): void {
    if (!isSupported(lang)) {
      return;
    }

    const stored = this.read();
    if (stored?.source === 'user' || (stored?.source === 'relation' && stored.lang === lang)) {
      return;
    }

    if (this.write({ lang, source: 'relation' }) && lang !== this.current) {
      this._reload();
    }
  }

  /** Remembers the user's choice and reloads the portal in that language. */
  public switchTo(lang: PortalLanguage): void {
    if (this.write({ lang, source: 'user' }) && lang !== this.current) {
      this._reload();
    }
  }

  private browserLanguage(): PortalLanguage | null {
    const lang = this._browserLanguage()?.split('-')[0]?.toLowerCase();
    return isSupported(lang) ? lang : null;
  }

  private read(): StoredLanguage | null {
    try {
      const stored = JSON.parse(localStorage.getItem(LANGUAGE_STORAGE_KEY) ?? 'null') as StoredLanguage | null;
      return isSupported(stored?.lang) && (stored.source === 'user' || stored.source === 'relation') ? stored : null;
    } catch {
      return null;
    }
  }

  private write(value: StoredLanguage): boolean {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, JSON.stringify(value));
      return true;
    } catch {
      return false;
    }
  }
}

function isSupported(lang: string | null | undefined): lang is PortalLanguage {
  return SUPPORTED_LANGUAGES.includes(lang as PortalLanguage);
}
